/**
 * Salesforce Authentication Service
 * Handles JWT token generation and management for authenticated conversations
 */

import { SALESFORCE_CONSTANTS, SALESFORCE_CONFIG, validateSalesforceConfig } from './constants';
import { getCognitoIdToken } from './cognitoService';
import { 
  storeJwt, 
  getJwt, 
  clearJwt, 
  storeLastEventId, 
  getLastEventId,
  getStoredSalesforceConfig,
  storeSalesforceConfig
} from './webStorageService';

// Token request cache to prevent multiple simultaneous requests
let tokenRequestPromise = null;
let tokenRequestTimestamp = null;
const TOKEN_REQUEST_TIMEOUT = 30000; // 30 seconds

/**
 * Clear the token request cache
 * Call this when starting a new conversation to ensure a fresh token is obtained
 */
export const clearTokenCache = () => {
  console.log('[Salesforce:Auth] Clearing token request cache');
  tokenRequestPromise = null;
  tokenRequestTimestamp = null;
};

/**
 * Send HTTP request with error handling
 */
const sendRequest = async (url, options = {}) => {
  try {
    const response = await fetch(url, {
      mode: 'cors',
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    // Handle unauthorized requests
    if (response.status === 401) {
      console.warn('Unauthorized Salesforce request, clearing stored JWT');
      clearJwt();
      throw new Error('Unauthorized: JWT token invalid or expired');
    }

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = { message: `HTTP ${response.status}: ${response.statusText}` };
      }
      
      const error = new Error(`Salesforce API Error: ${response.status}`);
      error.response = response;
      error.data = errorData;
      throw error;
    }

    return response.json();
  } catch (error) {
    console.error('Salesforce Request failed:', error);
    throw error;
  }
};

/**
 * Get authenticated access token using Cognito JWT
 * PRIMARY function for authenticated Support Chat
 * 
 * @param {Object} config - Salesforce configuration (optional)
 * @returns {Promise<{accessToken: string, expiresIn: number}>}
 */
export const getAuthenticatedAccessToken = async (config = null) => {
  try {
    console.log('[Salesforce:Auth] Getting authenticated access token with Cognito JWT');

    // Check if there's already a token request in progress
    if (tokenRequestPromise && tokenRequestTimestamp) {
      const timeElapsed = Date.now() - tokenRequestTimestamp;
      if (timeElapsed < TOKEN_REQUEST_TIMEOUT) {
        return await tokenRequestPromise;
      } else {
        tokenRequestPromise = null;
        tokenRequestTimestamp = null;
      }
    }

    // Check if we already have a valid Salesforce token
    const existingToken = getJwt();
    
    if (existingToken) {
      // Validate token is not expired
      try {
        const payload = JSON.parse(atob(existingToken.split('.')[1]));
        const exp = payload.exp * 1000; // Convert to milliseconds
        const now = Date.now();
        const bufferMs = 60000; // 1 minute buffer
        
        if (exp > now + bufferMs) {
          console.log('[Salesforce:Auth] Using existing valid Salesforce access token');
          console.log('[Salesforce:Auth] Token expires at:', new Date(exp).toISOString());
          return { accessToken: existingToken };
        } else {
          console.log('[Salesforce:Auth] Existing token expired or expiring soon, getting new one');
          clearJwt(); // Clear the expired token
        }
      } catch (e) {
        console.warn('[Salesforce:Auth] Could not validate existing token, getting new one:', e.message);
        clearJwt(); // Clear the invalid token
      }
    }

    // Create a new token request and cache the promise
    tokenRequestTimestamp = Date.now();
    tokenRequestPromise = performAuthenticatedTokenRequest(config);

    try {
      const result = await tokenRequestPromise;
      // Clear the cache after successful completion
      tokenRequestPromise = null;
      tokenRequestTimestamp = null;
      return result;
    } catch (error) {
      // Clear the cache on error so retry is allowed
      tokenRequestPromise = null;
      tokenRequestTimestamp = null;
      throw error;
    }
  } catch (error) {
    console.error('[Salesforce:Auth] Failed to get authenticated access token:', error);
    throw error;
  }
};

/**
 * Perform authenticated token request
 * Gets Cognito JWT and sends to Salesforce Salesforce v2 API
 * @private
 */
const performAuthenticatedTokenRequest = async (config = null) => {
  const salesforceConfig = config || getStoredSalesforceConfig();
  
  if (!salesforceConfig) {
    throw new Error('Salesforce configuration not found');
  }

  validateSalesforceConfig(salesforceConfig);
  
  const { API_BASE_URL, ORGANIZATION_ID, DEVELOPER_NAME } = salesforceConfig;

  // Step 1: Get fresh Cognito ID Token (JWT) from AWS Cognito
  // Automatically refreshes if expired
  const cognitoIdToken = await getCognitoIdToken();
  
  console.log('[Salesforce:Auth] Got Cognito JWT, sending to Salesforce...');
  
  const url = `${API_BASE_URL}${SALESFORCE_CONSTANTS.API_PATHS.AUTHENTICATED_TOKEN}`;

  // Step 2: Send Cognito JWT to Salesforce Salesforce v2 API
  // Salesforce will validate JWT via JWKS and extract user claims
  const payload = {
    orgId: ORGANIZATION_ID,                    // Required: Salesforce org ID
    esDeveloperName: DEVELOPER_NAME,           // Required: Embedded Service deployment name
    authorizationType: 'JWT',                  // Required: Must be 'JWT' for authenticated mode
    customerIdentityToken: cognitoIdToken,     // Required: Cognito ID Token (JWT)
    platform: 'Web',                           // Required: Platform identifier
    capabilitiesVersion: '1'                   // Required: API capabilities version
  };

  const data = await sendRequest(url, {
    method: 'POST',
    body: JSON.stringify(payload)
  });

  if (!data.accessToken) {
    throw new Error('No access token in response');
  }

  // Step 3: Store the Salesforce messaging token (NOT the Cognito token)
  // This token is used for all subsequent messaging API calls
  console.log('[Salesforce:Auth] Storing Salesforce access token (first 50 chars):', data.accessToken.substring(0, 50) + '...');
  storeJwt(data.accessToken);
  
  // Verify token was stored correctly
  const verifyStored = getJwt();
  if (verifyStored !== data.accessToken) {
    console.error('[Salesforce:Auth] Token storage verification FAILED!');
    console.error('[Salesforce:Auth] Stored:', verifyStored?.substring(0, 50));
    console.error('[Salesforce:Auth] Expected:', data.accessToken.substring(0, 50));
  } else {
    console.log('[Salesforce:Auth] Token storage verified successfully');
  }

  if (data.lastEventId) {
    storeLastEventId(data.lastEventId);
  }

  console.log('[Salesforce:Auth] Authenticated access token obtained successfully');
  if (data.expiresIn) {
    console.log('[Salesforce:Auth] Token expires in:', data.expiresIn, 'seconds');
  }

  return data;
};

/**
 * Get unauthenticated JWT token for anonymous conversations
 * DEPRECATED: Will be removed - use getAuthenticatedAccessToken() instead
 */
export const getUnauthenticatedAccessToken = async (config = null) => {
  try {
    // Check if there's already a token request in progress
    if (tokenRequestPromise && tokenRequestTimestamp) {
      const timeElapsed = Date.now() - tokenRequestTimestamp;
      if (timeElapsed < TOKEN_REQUEST_TIMEOUT) {
        return await tokenRequestPromise;
      } else {
        tokenRequestPromise = null;
        tokenRequestTimestamp = null;
      }
    }

    // Check if we already have a valid token
    const existingToken = getJwt();
    if (existingToken) {
      return { accessToken: existingToken };
    }

    // Create a new token request and cache the promise
    tokenRequestTimestamp = Date.now();
    tokenRequestPromise = performTokenRequest(config);

    try {
      const result = await tokenRequestPromise;
      // Clear the cache after successful completion
      tokenRequestPromise = null;
      tokenRequestTimestamp = null;
      return result;
    } catch (error) {
      // Clear the cache on error so retry is allowed
      tokenRequestPromise = null;
      tokenRequestTimestamp = null;
      throw error;
    }
  } catch (error) {
    console.error('[Salesforce:Auth] Failed to get unauthenticated access token:', error);
    throw error;
  }
};

/**
 * Perform the actual token request (extracted for caching)
 */
const performTokenRequest = async (config = null) => {
  // Use provided config or get from environment/storage
  const salesforceConfig = config || getStoredSalesforceConfig() || SALESFORCE_CONFIG;
  
  // Validate configuration
  validateSalesforceConfig(salesforceConfig);
  
  const { ORGANIZATION_ID, DEVELOPER_NAME, API_BASE_URL } = salesforceConfig;
  
  // Always store configuration for future use (even if provided, so it's available for subsequent calls)
  await storeSalesforceConfig(salesforceConfig);

  const url = `${API_BASE_URL}${SALESFORCE_CONSTANTS.API_PATHS.UNAUTHENTICATED_TOKEN}`;
  
  const requestBody = {
    orgId: ORGANIZATION_ID,
    esDeveloperName: DEVELOPER_NAME,
    capabilitiesVersion: SALESFORCE_CONSTANTS.CAPABILITIES_VERSION,
    platform: SALESFORCE_CONSTANTS.PLATFORM
  };
  
  const response = await sendRequest(url, {
    method: 'POST',
    mode: 'cors',
    body: JSON.stringify(requestBody)
  });

  // Store the JWT and last event ID
  const { accessToken, lastEventId } = response;
  
  if (accessToken) {
    storeJwt(accessToken);
  }
  
  if (lastEventId) {
    storeLastEventId(lastEventId);
  }

  return response;
};

/**
 * Get continuation JWT token for session continuity
 * ⚠️ CRITICAL: This should be used for PAGE RELOAD scenarios when JWT already exists
 * API: GET /authorization/continuation-access-token
 */
export const getContinuityAccessToken = async () => {
  try {
    const salesforceConfig = getStoredSalesforceConfig();
    validateSalesforceConfig(salesforceConfig);
    
    const { API_BASE_URL } = salesforceConfig;
    const currentJwt = getJwt();
    
    if (!currentJwt) {
      console.error('[Salesforce:Auth] Cannot get continuation token: No existing JWT');
      throw new Error('No existing JWT token for continuation');
    }
    
    const url = `${API_BASE_URL}${SALESFORCE_CONSTANTS.API_PATHS.CONTINUATION_TOKEN}`;
    
    // IMPORTANT: Continuation token uses GET with Authorization header
    const response = await sendRequest(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Authorization': `Bearer ${currentJwt}`
      }
    });

    // Store the new JWT and last event ID
    const { accessToken, lastEventId} = response;
    
    if (accessToken) {
      storeJwt(accessToken);
      
      // CRITICAL: Verify storage
      const verifyJwt = getJwt();
      if (verifyJwt !== accessToken) {
        console.error('[Salesforce:Auth] JWT storage verification FAILED');
      }
    }
    
    if (lastEventId) {
      storeLastEventId(lastEventId);
    }

    return response;
  } catch (error) {
    console.error('[Salesforce:Auth] Failed to get continuation access token:', error);
    throw error;
  }
};

/**
 * Alias for getContinuityAccessToken (following sample app naming)
 * This is the recommended function name for page reload scenarios
 */
export const getContinuityJwt = getContinuityAccessToken;

/**
 * Initialize authentication
 * ONLY call this when starting a NEW conversation
 * For page reload (JWT exists), use getContinuityJwt() instead
 */
export const initializeAuthentication = async (config = null) => {
  try {
    clearJwt();
    await getUnauthenticatedAccessToken(config);
    return { success: true, isNew: true };
  } catch (error) {
    console.error('Failed to initialize Salesforce authentication:', error);
    throw error;
  }
};

/**
 * Check if current JWT is valid (simple check)
 */
export const isJwtValid = () => {
  const jwt = getJwt();
  if (!jwt) return false;

  try {
    // Basic JWT structure check (header.payload.signature)
    const parts = jwt.split('.');
    if (parts.length !== 3) {
      console.warn('JWT validation failed: Invalid JWT structure');
      return false;
    }

    // Try to decode payload with better error handling
    try {
      // Add padding if needed for base64 decoding
      let payload = parts[1];
      while (payload.length % 4) {
        payload += '=';
      }
      
      const decodedPayload = JSON.parse(atob(payload));
      
      // Check expiration if present
      if (decodedPayload.exp) {
        const now = Math.floor(Date.now() / 1000);
        if (decodedPayload.exp < now) {
          console.warn('JWT token has expired');
          clearJwt();
          return false;
        }
      }

      return true;
    } catch (decodeError) {
      console.warn('JWT payload decode failed:', decodeError);
      // Don't clear JWT immediately, maybe it's just validation that's failing
      // Let the API calls determine if the token is actually invalid
      return true; // Be permissive like the sample app
    }

  } catch (error) {
    console.warn('JWT validation failed:', error);
    return true; // Be permissive like the sample app - let API calls fail if token is bad
  }
};

/**
 * Get current JWT with validation
 */
export const getCurrentJwt = () => {
  return getJwt() || null;
};

/**
 * Clear authentication data
 */
export const clearAuthentication = () => {
  clearJwt();
};